import { MemoryStorage } from './MemoryStorage'

// NOTE: sessionStorageService (instantiated at the bottom of this file) is the
// ONLY way that localStorage should be accessed. This will prevent user
// session contamination across tabs.

export enum SessionKey {
  ATTORNEY_EMAIL_KEY = 'email',
  ATTORNEY_ID_KEY = 'attorney-id',
  ATTORNEY_PROFILE_ID_KEY = 'profile-id',
  CHAT_KEY = 'chat-key',
  IS_QUALIFIED_ATTORNEY_KEY = 'is_qualified_attorney',
  IS_VETTED_ATTORNEY_KEY = 'is_vetted_attorney',
  PROFILE_CONSISTENCY_TOKEN = '__consistencyToken',
  REFRESH_TOKEN = 'refresh_token',
  TOKEN = 'token',
  DISMISSED_FEATURES_KEY = 'dismissed_features',
  MY_REFERRALS_SELECTED_CATEGORY = 'my-referrals-selected-category',
  HOW_IT_WORKS_DISMISSED = 'how-it-works-dismissed',
  /**
   * Refers to the "src" query parameter value that the current session booted
   * with. This is typically provided from an LCMS integration.
   */
  SESSION_SRC = 'session_src',
  LCMS_CASE_ID = 'lcms_case_id',
  /** Ask AttorneyShare's conversation; wiped with the session on sign-out. */
  ASSISTANT_HISTORY = 'assistant-history',
  /**
   * Unmatch & Repost (ENG-1708): the reposted case whose profile still owes
   * the user a "Case reposted." toast. One case id; wiped on sign-out.
   */
  REPOSTED_CASE_TOAST = 'reposted-case-toast',
  /**
   * ENG-2173: source cases this device just reposted, with the time. The
   * Inactive row hides Repost Case for them while the pipeline creates the
   * new case. JSON map of case id to epoch ms; wiped on sign-out.
   */
  REPOST_PENDING = 'repost-pending',
}

/**
 * Keys that should persist across sign-out/sign-in sessions
 */
const PERSISTENT_KEYS: readonly SessionKey[] = [
  SessionKey.MY_REFERRALS_SELECTED_CATEGORY,
  SessionKey.HOW_IT_WORKS_DISMISSED,
] as const

/**
 * Raw localStorage keys (outside the SessionKey enum) that must SURVIVE
 * `clear()` because they bridge data across auth transitions — e.g. the
 * onboarding practice-area suggestion that's written after account
 * creation and read after email verification, with intermediate
 * `clear()` calls in useVerification + useAuthentication.
 *
 * Safety: nothing in this list should ever hold a token or PII. The
 * security guarantee of `clear()` is that auth state is fully wiped;
 * preserving non-auth bridge data does not weaken that.
 */
const CROSS_AUTH_PRESERVED_RAW_KEYS: readonly string[] = [
  'attorneyshare:pending-pa-suggestion',
  // ENG-1955: the ad click ID captured at boot, read back when the account
  // is created. handleCreateOrg calls clear() immediately before
  // createAccount, so without this the attribution is wiped one line
  // before it is needed. Holds an opaque ad identifier -- no token, no PII.
  'attorneyshare:signup-attribution',
] as const

export class SessionStorageService {
  private storage: Storage

  /**
   * Pure check: returns true if the in-memory email matches localStorage.
   * No side effects — does not redirect or call external handlers.
   * Cross-tab redirect is handled exclusively by TokenManager's `storage`
   * event listener, which avoids circular callbacks and ensures storage
   * operations (especially `clear()`) always complete.
   */
  private isSessionConsistent = (): boolean => {
    const storedEmail = this.storage.getItem(SessionKey.ATTORNEY_EMAIL_KEY)
    const { loggedInEmail } = this

    if (loggedInEmail === null || storedEmail === null) {
      return true
    }

    return storedEmail === loggedInEmail
  }

  private loggedInEmail: string | null

  constructor() {
    try {
      // NOTE: This will expectedly throw an error in environments where
      // localStorage is not available.
      const testKey = `__storage-test`

      // eslint-disable-next-line no-restricted-properties
      window.localStorage.setItem(testKey, '')
      // eslint-disable-next-line no-restricted-properties
      window.localStorage.removeItem(testKey)

      // eslint-disable-next-line no-restricted-properties
      this.storage = window.localStorage
    } catch (e) {
      this.storage = new MemoryStorage()
    }

    this.loggedInEmail = this.storage.getItem(SessionKey.ATTORNEY_EMAIL_KEY)
  }

  getItem = (key: SessionKey): string | null => {
    if (!this.isSessionConsistent()) return null

    return this.storage.getItem(key)
  }

  setItem = (key: SessionKey, value: string) => {
    if (key === SessionKey.ATTORNEY_EMAIL_KEY) {
      this.loggedInEmail = value
      this.storage.setItem(key, value)

      return
    }

    if (!this.isSessionConsistent()) return

    this.storage.setItem(key, value)
  }

  removeItem = (key: SessionKey) => {
    if (!this.isSessionConsistent()) return

    this.storage.removeItem(key)
  }

  /**
   * Clears all storage unconditionally. No consistency check — teardown
   * operations (logout, refresh failure) must always be able to clear
   * stale tokens. Without this guarantee, stale tokens survive the clear
   * and cause redirect loops between /signIn and the authenticated app.
   *
   * Keys in CROSS_AUTH_PRESERVED_RAW_KEYS are snapshotted before the
   * wipe and re-written after, so onboarding bridge data survives the
   * auth transition without weakening the auth-state guarantee.
   */
  clear = () => {
    const preserved = new Map<string, string>()
    CROSS_AUTH_PRESERVED_RAW_KEYS.forEach(key => {
      const value = this.storage.getItem(key)
      if (value !== null) preserved.set(key, value)
    })
    this.loggedInEmail = null
    this.storage.clear()
    preserved.forEach((value, key) => {
      this.storage.setItem(key, value)
    })
  }

  /**
   * Gets persistent values before clearing operations
   */
  getPersistentValues = (): Map<SessionKey, string> => {
    const values = new Map<SessionKey, string>()
    PERSISTENT_KEYS.forEach(key => {
      const value = this.storage.getItem(key)
      if (value !== null) {
        values.set(key, value)
      }
    })
    return values
  }

  /**
   * Restores persistent values after clearing operations
   */
  restorePersistentValues = (values: Map<SessionKey, string>) => {
    values.forEach((value, key) => {
      this.storage.setItem(key, value)
    })
  }
}

export const sessionStorageService = new SessionStorageService()
